import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { fullName } from "../../utils/helpers/userDisplay";
import Avatar from "../Avatar/Avatar";
import ScoreBadge from "../ScoreBadge/ScoreBadge";
import { scoreHex, scoreTint } from "../../utils/helpers/scoreColors";
import { COUNTED_OBSERVATION_LIMIT, num } from "../../utils/helpers/scoring";
import { formatShortDate } from "../../utils/helpers/dateDisplay";
import { compareText } from "../../utils/helpers/locale";
import { standardAppliesTo, standardScopeLabel } from "../../utils/helpers/standardScope";

// Pale band fill from the shared tier table (was a hardcoded copy of the
// breakpoints + hexes).
const cellBackground = (pct) => scoreTint(pct);

// A dot with no score to color: an empty window slot, or a lesson the
// standard's scope rules out (dashed, as in the teacher modal's matrix).
const DOT_CLASS = {
  empty: "bg-white/80 ring-1 ring-slate-300/70",
  notScored: "border border-dashed border-slate-400",
};

function SortIcon({ direction }) {
  return (
    <svg
      className="w-3 h-3 ml-1 text-slate-500"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      {direction === "descending" ? (
        <path strokeLinecap="round" strokeWidth="3" d="M19 9l-7 7-7-7" />
      ) : (
        <path strokeLinecap="round" strokeWidth="3" d="M5 15l7-7 7 7" />
      )}
    </svg>
  );
}

// `teachers` is the TeacherAnalyticsSerializer payload:
//   [{ teacher: AccountMini, avg_percentage, observation_count,
//      per_standard: {std_uuid: pct},
//      counted_observations: [{uuid, observed_at, percentage, per_standard,
//                               domain_subject: subject uuid | null}]
//      — the counted window's observations, oldest → newest }]
// `standards` is the active rubric's standards:
//   [{uuid, number, title, order, subject_scope, subjects}].
export default function TeacherDirectory({
  standards = [],
  teachers = [],
  onCellClick,
  onNameClick,
}) {
  const { t } = useTranslation();
  const [sortConfig, setSortConfig] = useState({
    key: "observations",
    direction: "descending",
  });
  // Cell display values are derived here rather than in the row renderer, and
  // WITHOUT sortConfig, so a sort click reorders finished rows instead of
  // redoing tint/title/date work for every teacher × standard.
  const shaped = useMemo(() => {
    const notEvaluated = t("heatmap.notEvaluated");
    const noObservation = t("heatmap.noObservation");
    const notScoredHere = t("subjectScope.notScoredHere");
    return teachers.map((row) => {
      const tch = row.teacher || {};
      const observed = row.observation_count ?? 0;
      const tName = tch.full_name || fullName(tch);
      const counted = row.counted_observations || [];
      // The dot's date is the same in every standard's column — format it once.
      const dotDates = counted.map((obs) => formatShortDate(obs.observed_at));
      // The column sorts read this, so the comparator never re-coerces the
      // payload's decimal strings — and sorts on the unrounded value the way it
      // always did, so two cells that round alike still order apart.
      const scores = {};
      return {
        teacher: tch,
        tName,
        overall: num(row.avg_percentage),
        observed,
        unevaluated: observed === 0,
        scores,
        cells: standards.map((std) => {
          const pct = num(row.per_standard?.[std.uuid]);
          scores[std.uuid] = pct;
          const rounded = pct == null ? null : Math.round(pct);
          return {
            std,
            background: cellBackground(rounded),
            title: `${tName} — S${std.number}: ${
              rounded == null ? notEvaluated : `${rounded}%`
            }`,
            dots: Array.from({ length: COUNTED_OBSERVATION_LIMIT }, (_, i) => {
              const obs = counted[i];
              if (!obs) {
                return { key: `slot-${i}`, kind: "empty", title: noObservation };
              }
              if (!standardAppliesTo(std, obs.domain_subject)) {
                return {
                  key: obs.uuid,
                  kind: "notScored",
                  title: `${dotDates[i]} — ${notScoredHere}`,
                };
              }
              const dotPct = num(obs.per_standard?.[std.uuid]);
              return {
                key: obs.uuid,
                hex: scoreHex(dotPct),
                title: `${dotDates[i]} — ${
                  dotPct == null ? notEvaluated : `${Math.round(dotPct)}%`
                }`,
              };
            }),
          };
        }),
      };
    });
  }, [teachers, standards, t]);

  const sorted = useMemo(() => {
    const { key, direction } = sortConfig;
    if (!key) return shaped;
    const mul = direction === "ascending" ? 1 : -1;
    return [...shaped].sort((a, b) => {
      if (key === "teacherName") {
        return mul * compareText(a.tName || "", b.tName || "");
      }
      if (key === "overallScore") {
        return mul * ((a.overall ?? -1) - (b.overall ?? -1));
      }
      if (key === "observations") {
        return mul * (a.observed - b.observed);
      }
      // standard uuid key
      return mul * ((a.scores[key] ?? -1) - (b.scores[key] ?? -1));
    });
  }, [shaped, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "ascending" ? "descending" : "ascending",
        };
      }
      return { key, direction: key === "teacherName" ? "ascending" : "descending" };
    });
  };

  const gridColsClass = `repeat(${standards.length}, 3.5rem)`;

  return (
    <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden flex flex-col w-fit mx-auto">
      <div className="px-6 py-4 border-b border-slate-100 bg-white flex items-center justify-end gap-4">
        <div className="px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-[10px] font-bold uppercase tracking-wider">
          {t("heatmap.teachers", { count: sorted.length })}
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="inline-block align-middle">
          <div className="flex bg-slate-50/50 border-b border-slate-200 sticky top-0 z-20 backdrop-blur-sm">
            <div className="w-64 shrink-0 px-6 py-4 font-bold text-slate-500 text-left sticky left-0 bg-white/90 z-10 flex items-center">
              <button
                type="button"
                onClick={() => handleSort("teacherName")}
                className="flex items-center group text-xs uppercase tracking-widest"
              >
                {t("heatmap.colTeacher")}
                {sortConfig.key === "teacherName" && (
                  <SortIcon direction={sortConfig.direction} />
                )}
              </button>
            </div>
            <div className="w-20 shrink-0 px-1 py-4 font-bold text-slate-500 text-center flex items-center justify-center bg-slate-50/50">
              <button
                type="button"
                onClick={() => handleSort("overallScore")}
                className="flex items-center group text-[10px] uppercase tracking-widest"
              >
                {t("heatmap.colOverall")}
                {sortConfig.key === "overallScore" && (
                  <SortIcon direction={sortConfig.direction} />
                )}
              </button>
            </div>
            <div className="w-20 shrink-0 px-1 py-4 font-bold text-slate-500 text-center flex items-center justify-center bg-slate-50/50 border-r border-slate-200">
              <button
                type="button"
                onClick={() => handleSort("observations")}
                className="flex items-center group text-[10px] uppercase tracking-widest"
              >
                {t("heatmap.colObs")}
                {sortConfig.key === "observations" && (
                  <SortIcon direction={sortConfig.direction} />
                )}
              </button>
            </div>
            <div
              className="grid gap-px bg-slate-100"
              style={{ gridTemplateColumns: gridColsClass }}
            >
              {standards.map((std) => {
                const scopeLabel = standardScopeLabel(t, std);
                return (
                  <div key={std.uuid} className="bg-slate-50/50">
                    {/* A subject-scoped column is blank for everyone teaching
                        something else; the dot and the tooltip say why. */}
                    <button
                      type="button"
                      onClick={() => handleSort(std.uuid)}
                      className="group font-bold text-slate-500 hover:bg-white transition-colors flex items-center justify-center w-full h-full py-4"
                      title={[std.title, scopeLabel].filter(Boolean).join(" — ")}
                    >
                      <span className="font-mono text-xs">S{std.number}</span>
                      {scopeLabel && (
                        <span className="ml-1 w-1.5 h-1.5 rounded-full bg-indigo-400" aria-hidden="true" />
                      )}
                      {sortConfig.key === std.uuid && (
                        <SortIcon direction={sortConfig.direction} />
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            {sorted.length === 0 && (
              <div className="px-6 py-10 text-center text-sm text-slate-400">
                {t("heatmap.noMatch")}
              </div>
            )}
            {sorted.map((row) => (
              <div
                key={row.teacher.uuid}
                className={`flex border-b border-slate-50 last:border-b-0 group h-14 items-stretch ${
                  row.unevaluated ? "bg-slate-50/30" : "bg-white"
                }`}
              >
                <div
                  className={`w-64 shrink-0 px-6 text-sm font-medium truncate sticky left-0 group-hover:bg-slate-50 z-10 flex items-center gap-4 ${
                    row.unevaluated ? "bg-slate-50/30" : "bg-white"
                  }`}
                >
                  <Avatar
                    user={row.teacher}
                    className="relative w-10 h-10 rounded-full shrink-0 border-2 border-slate-200 shadow-sm bg-slate-700 text-white text-[10px] font-bold"
                  />
                  <div className="flex flex-col min-w-0 flex-1">
                    <button
                      type="button"
                      onClick={() => onNameClick?.(row.teacher)}
                      className="text-left truncate transition-colors text-slate-800 hover:text-blue-600 font-bold text-sm"
                    >
                      {row.tName}
                    </button>
                  </div>
                </div>
                <div className="w-20 shrink-0 px-4 py-3 flex items-center justify-center border-r border-slate-100 bg-slate-50/30">
                  <ScoreBadge value={row.overall} />
                </div>
                <div className="w-20 shrink-0 px-4 py-3 flex items-center justify-center border-r border-slate-100 bg-slate-50/30">
                  <span className="text-xs font-bold text-slate-600">
                    {row.observed}
                  </span>
                </div>
                <div
                  className="grid gap-px bg-slate-50"
                  style={{ gridTemplateColumns: gridColsClass }}
                >
                  {row.cells.map((cell) => (
                    <div
                      key={cell.std.uuid}
                      className="bg-white group-hover:bg-slate-50/50 relative flex items-center justify-center transition-colors"
                    >
                      <button
                        type="button"
                        onClick={() => onCellClick?.(row.teacher, cell.std)}
                        className="w-full h-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset transition-all flex items-center justify-center gap-1.5"
                        title={cell.title}
                        style={{ backgroundColor: cell.background }}
                      >
                        {cell.dots.map((dot) => (
                          <span
                            key={dot.key}
                            className={`w-2.5 h-2.5 rounded-full shrink-0 ${DOT_CLASS[dot.kind] || ""}`}
                            title={dot.title}
                            style={dot.hex ? { backgroundColor: dot.hex } : undefined}
                          />
                        ))}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
